(ns ui.view.entity-creator.opportunity.view
  (:require
   [clojure.string :as string]
   [reagent.core :as r]
   [re-frame.core :as rf]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [common.schema.entity-creator.utils :as ecu]
   [common.schema.entity-type-names :as etn]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [ui.completion.new-entity :as new-entity]
   [frontend.utils :as fu]
   [potpuri.core :as potpuri :refer [fn->]]
   [ui.components.breadcrumbs.view :as breadcrumbs]
   [ui.components.button.view :as button]
   [ui.components.drawer.view :as drawer]
   [ui.components.modal.view :as modal]
   [ui.components.search-selector.view :as search-selector]
   [ui.components.text.view :as text]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.dom-globals :as dg]
   [ui.editors.entity-creator.utils :as editor-utils]
   [ui.editors.data-input :as data-input]
   [ui.editors.labelled-input.view :as input]
   [ui.state.session.sub :as session-sub]
   [ui.view.entity-creator.components :as creator-components]
   [ui.view.entity-creator.view :as creator]
   [ui.view.entity-creator.model :as creator-m]
   [ui.view.entity-creator.utils :as creator-u]
   [ui.view.entity-creator.opportunity.event]
   [ui.view.entity-creator.opportunity.sub]
   [ui.view.entity-creator.opportunity.model :as m]
   [ui.view.entity-creator.editor :as editor]
   [ui.util :as uu]))

(defn result-item
  [{:keys [result on-select selected?]}]
  (let [{:keys [_id _source _extras]} result
        original (-> _source :original)
        et (-> original :entity/type keyword)
        et-name (etn/get-name et :en)
        entity (assoc original :entity/type et)
        icon (entity/default-icon entity)
        email (:person/universal-email-address original)
        title (entity/display-string entity)]
    [:div
     {:class (uu/cm "flex flex-col  px-4 py-2"
                    (when selected?
                      "bg-primary-light")
                    "hover:bg-base-regular hover:cursor-pointer")
      :on-click (fn [_]
                  ;; Only the id. The row holds a few display fields, and for a
                  ;; company only its name. The caller loads the entity from the
                  ;; backend.
                  (on-select (:db/id original)))}
     [:div
      {:class (uu/cm "font-semibold pb-1")}
      title]
     [:div
      {:class (uu/cm "flex gap-1 text-base-regular-text-paler text-sm m-0")}
      [:i
       {:class (uu/cm "fa fal" icon)}]
      [:div et-name]
      (when email
        [:div " · " email])]]))

(defn target-label-loc-key
  [entity-type]
  (case entity-type
    :entity.type/fundraising-opportunity :creator/investor-vehicle
    :entity.type/oppo :target-asset
    :entity.type/sales-opportunity :sales-customer
    :entity.type/exit-opportunity :buyer
    :target))

(defn target-title-loc-key
  "Section-header variant of `target-label-loc-key` — differs from the input
   label only for froppo, where the header uses the slashless
   `:investor-vehicle` (\"Investor vehicle\") instead of the picker-label
   `:creator/investor-vehicle` (\"Investor / vehicle\")."
  [entity-type]
  (case entity-type
    :entity.type/fundraising-opportunity :investor-vehicle
    :entity.type/oppo :target-asset
    :entity.type/sales-opportunity :sales-customer
    :entity.type/exit-opportunity :buyer
    :target))

(defn default-title-loc-key
  [entity-type]
  (case entity-type
    :entity.type/fundraising-opportunity :creator/default-froppo-title
    :entity.type/oppo :creator/default-oppo-title
    :entity.type/sales-opportunity :creator/default-sales-oppo-title
    :entity.type/exit-opportunity :creator/default-exit-oppo-title))

(defn create-for-target-loc-key
  [entity-type]
  (case entity-type
    :entity.type/fundraising-opportunity :creator/create-froppo-for-investor-vehicle
    :entity.type/oppo :creator/create-oppo-for-target-asset
    :entity.type/sales-opportunity :creator/create-sales-oppo-for-customer
    :entity.type/exit-opportunity :creator/create-exit-oppo-for-buyer))

(defn without-target-loc-key
  [entity-type]
  (case entity-type
    :entity.type/fundraising-opportunity :creator/continue-without-investor-vehicle
    :entity.type/oppo :creator/continue-without-target-asset
    :entity.type/sales-opportunity :creator/continue-without-customer
    :entity.type/exit-opportunity :creator/continue-without-buyer
    :creator/skip))

(def target-create-button-configs
  "Per target entity-type config for the inline 'New X' buttons under the
   target-search box. `:match-attribute` is the field on existing search
   results compared against the typed query to decide whether to seed the
   nested creator with the query string."
  {:entity.type/person {:icon-class "fa-user"
                        :label-key :new-contact
                        :match-attribute :person/universal-email-address}
   :entity.type/company {:icon-class "fa-building"
                         :label-key :new-company
                         :match-attribute :asset/name}
   :entity.type/real-estate {:icon-class "fa-house"
                             :label-key :new-real-estate
                             :match-attribute :asset/name}
   :entity.type/fof-asset {:icon-class "fa-seedling"
                           :label-key :new-fund
                           :match-attribute :asset/name}
   :entity.type/other-asset {:icon-class "fa-dna"
                             :label-key :new-generic-asset
                             :match-attribute :asset/name}})

(def target-types-with-create-buttons
  "Stable render order for the 'New X' button row."
  [:entity.type/person
   :entity.type/company
   :entity.type/real-estate
   :entity.type/fof-asset
   :entity.type/other-asset])

(defn create-target-button
  [{:keys [args local-state target-type config]}]
  (let [{:keys [icon-class label-key match-attribute]} config]
    [button/secondary-hollow
     {:class (uu/cm "bg-primary-light text-primary-regular"
                    "flex-1 min-w-40 truncate")
      :on-click (fn [_]
                  (let [{:keys [query results]} @local-state
                        lc-query (cu/str-trim-lcase query)
                        query? (not (cu/blank-value? query))
                        match? (->> results
                                    (some (fn-> :_source
                                                :original
                                                match-attribute
                                                cu/str-trim-lcase
                                                (= lc-query))))]
                    (rf/dispatch
                     [m/confirm-target
                      (cond-> args
                        true (assoc :target-type target-type)

                        (and query?
                             (not match?)) (assoc :initial-value query))])))}
     [:i {:class (uu/cm "fa" icon-class)}]
     (loc/tr label-key)]))

(defn select-target-view
  [{:keys [_ident local-state] :as args}]
  (r/with-let [searchbox-id "target-search-selector"
               local-state (or local-state (r/atom {}))
               ;; will update later: more comprehensive UI for opensearch-results:
               _open! (fn []
                        (swap! local-state assoc :expanded? true))
               unfocus! (fn []
                          (some-> searchbox-id
                                  dg/element-by-id
                                  (.blur)))

               results* (r/cursor local-state [:results])
               query* (r/cursor local-state [:query])
               ;; the id of the target that is loading from the backend, or nil
               loading* (r/cursor local-state [:loading-id])
               _ (when @(rf/subscribe [m/selected-target args])
                   (swap! local-state assoc
                          :query nil
                          :expanded? false
                          :results nil))
               _close! (fn []
                         (swap! local-state
                                (fn [state]
                                  (-> state
                                      (assoc
                                       :expanded? false
                                       :show-results? false)
                                      (dissoc :query))))
                         ;;(rf/dispatch-sync [m/update-query args nil])
                         (unfocus!))]
    (let [target @(rf/subscribe [m/selected-target args])
          entity-type @(rf/subscribe [m/entity-type args])
          label (loc/tr (target-label-loc-key entity-type))
          placeholder (case entity-type
                        :entity.type/fundraising-opportunity
                        (loc/tr :creator/select-investor-vehicle)

                        :entity.type/sales-opportunity
                        (loc/tr :creator/select-customer)

                        :entity.type/oppo
                        (loc/tr :creator/select-target-asset)

                        :entity.type/exit-opportunity
                        (loc/tr :creator/select-buyer)

                        (loc/tr :select-placeholder))
          target-id (:db/id target)
          target-option (when target
                          {:value target-id
                           :label (entity/display-string target)})
          pipeline @(rf/subscribe [m/pipeline args])
          investor-only? (= (set (:pipeline/target-asset-types pipeline))
                            #{:entity.type/investor})
          target-asset-types (:pipeline/target-asset-types pipeline)
          target-asset-types (if (empty? target-asset-types)
                               [:entity.type/company]
                               target-asset-types)
          target-entity-types (case entity-type
                                :entity.type/fundraising-opportunity
                                [:entity.type/company :entity.type/person]

                                :entity.type/sales-opportunity
                                (if investor-only?
                                  [:entity.type/company :entity.type/person]
                                  (vec target-asset-types))

                                :entity.type/oppo
                                target-asset-types

                                :entity.type/exit-opportunity
                                [:entity.type/company :entity.type/person]

                                nil)
          target-entity-types-set (set target-entity-types)
          expanded? (:expanded? @local-state)
          mandatory? (not (:pipeline/allow-without-target? pipeline))
          results @results*
          loading-id @loading*
          no-results? (= [] results)
          show-create-buttons? (or no-results?
                                   ;; at least 3 characters typed:
                                   (<= 3 (count @query*)))
          _stage @(rf/subscribe [m/stage args])]
      [:div
       [search-selector/view
        {:searchbox-id searchbox-id
         :label label
         :search-on-focus? true
         :on-focus (fn []
                     (swap! local-state assoc :expanded? true))
         :on-blur (fn [_]
                    ;; TODO requires rethinking:
                    ;;(reset! query* nil)
                    )
         :value target-option
         :on-clear (fn []
                     (reset! query* nil)
                     (rf/dispatch-sync [m/select-target
                                        (assoc args
                                               :target-entity-id
                                               nil)]))
         :mandatory? mandatory?
         :state local-state
         :placeholder placeholder
         :entity-types target-entity-types}]
       [:div
        {:class (uu/cm "flex flex-col"
                       "border border-solid border-base-light-border-paler")}
        ;;(pr-str (first @results*))
        (when loading-id
          [:div
           {:class (uu/cm "px-4 py-2")}
           (loc/tr :loading)])
        (when expanded?
          (if no-results?
            [:div
             {:class (uu/cm "px-4 py-2")}
             (loc/tr :creator/no-results)]
            [:div
             {:class (uu/cm "flex flex-col max-h-[235px] overflow-auto")}
             (doall
              (for [{:keys [id] :as result} results]
                ^{:key (str id)}
                [result-item
                 {:result result
                  :selected? (or (= id target-id)
                                 (= id loading-id))
                  :on-select (fn [id]
                               (reset! query* nil)
                               ;; The next step needs the whole entity and its investor
                               ;; profile, if it has one. (A froppo, exit or sales deal
                               ;; refuses a second profile.) Load them from the backend,
                               ;; always, also when the session already holds the entity.
                               ;; A second click on the same result does not fetch again.
                               ;; If the load fails, say so and stay on the picker. Do not
                               ;; continue with the search row: it holds only the name.
                               (creator-u/load-selected!
                                {:loading* loading*
                                 :entity-id id
                                 :on-loaded (fn [entity _entities]
                                              (rf/dispatch-sync
                                               [m/confirm-target
                                                (assoc args
                                                       :target-entity-id id
                                                       :target-entity-data entity)]))
                                 :on-error  (fn [_error]
                                              (rf/dispatch [m/target-load-failed]))}))}]))]))
        (when show-create-buttons?
          [:div
           {:class (uu/cm "flex flex-wrap gap-2")}
           (doall
            (for [target-type target-types-with-create-buttons
                  :let [config (get target-create-button-configs target-type)]
                  :when (and config
                             (contains? target-entity-types-set target-type))]
              ^{:key (str target-type)}
              [create-target-button
               {:args args
                :local-state local-state
                :target-type target-type
                :config config}]))])]])))

;; used for making sure association source matches
;; entity-type, target-type, or target-profile-type
;; (changing sales-pipeline customer-types from investor-only to
;;  person/company might leave associations-config with phantom investor associations):
(defn resolve-association-entity-types
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        target-type @(rf/subscribe [m/target-type args])
        target-profile-type (ecu/resolve-target-profile-type
                             {:pipeline pipeline
                              :entity-type entity-type
                              :target-type target-type})]
    (cond-> #{entity-type}
      (keyword? target-type)
      (conj target-type)

      (keyword? target-profile-type)
      (conj target-profile-type))))

(defn existing-association-parties
  "Returns {:association-source <entity> :parties [<entity>...]} for parties
   already associated to the selected target or target-profile via the given
   association-key. The source is whichever of target / target-profile matches
   the association-key's source-entity-type and is an existing (saved) entity.
   Returns nil if there is no matching saved source."
  [args [source-type _ association-type :as _association-key]]
  (let [target @(rf/subscribe [m/selected-target args])
        target-id (:db/id target)
        existing-target? (number? target-id)
        target-type (:entity/type target)
        target-profile @(rf/subscribe [m/target-profile args])
        target-profile-id (:db/id target-profile)
        existing-target-profile? (number? target-profile-id)
        target-profile-type (:entity/type target-profile)
        target-association? (= target-type source-type)
        target-profile-association? (= target-profile-type source-type)
        association-source (cond (and target-association?
                                      existing-target?)
                                 target

                                 (and target-profile-association?
                                      existing-target-profile?)
                                 target-profile

                                 :else
                                 nil)]
    (when association-source
      {:association-source association-source
       :parties (->> (fu/related-entity-associations-by-association-type
                      association-type
                      (:db/id association-source))
                     (map (partial fu/association-target association-source))
                     (remove :common/disabled?)
                     distinct
                     doall)})))

(defn existing-associations-view
  "Inline 'Already associated' note shown between the field's label and the
   selectbox: one row per existing party. A party the user is also selecting in
   this flow (present in `value`) is highlighted as a would-be duplicate.
   Renders nothing if there are no existing parties."
  [parties value]
  (when (seq parties)
    (let [selected-ids (->> value (map :value) set)]
      [:div
       {:class (uu/cm "flex flex-wrap items-center gap-x-1 gap-y-0.5"
                      "text-sm text-base-regular-text-paler mb-0")}
       [:div (loc/tr :creator/already-associated)]
       (doall
        (for [party parties
              :let [name (entity/display-string party)
                    duplicate? (contains? selected-ids (:db/id party))]]
          ^{:key (str (:db/id party))}
          (if duplicate?
            [tooltip/view
             [:div
              {:class (uu/cm "flex items-center gap-1 px-1 rounded"
                             "bg-warning-light text-warning-light-text")}
              name
              [:i.fal.fa-info-circle]]
             [:div (loc/tr :creator/duplicate-association-warning [name])]]
            [:div
             {:class (uu/cm "px-1")}
             name])))])))

(defn contact-details-view
  [args]
  (r/with-let [target-type @(rf/subscribe [m/target-type args])
               previous-target-type @(rf/subscribe [m/associations-target-type args])
               reset? (or (not= previous-target-type target-type)
                          (= :NOT-SET previous-target-type))
               ;; reset associations if target-type has changed:
               _ (when reset?
                   (rf/dispatch-sync [m/reset-associations args])
                   (rf/dispatch [m/set-associations-target-type args]))]
    (let [pipeline @(rf/subscribe [m/pipeline args])
          stage-id @(rf/subscribe [m/stage-id args])
          selected-target @(rf/subscribe [m/selected-target args])
          setting-path (if (and pipeline stage-id)
                         [:stages stage-id :setting]
                         [:create :setting])
          config @(rf/subscribe [m/associations-config args])
          association-entity-types (resolve-association-entity-types args)
          associations (->> (:associations config)
                            (filterv (fn [a]
                                       (contains? ecu/visible-settings
                                                  (get-in a setting-path))))
                            (filterv (fn [{[source-entity-type _] :association-key}]
                                       (contains? association-entity-types
                                                  source-entity-type))))]
      [:div
       [:div
        {:class (uu/cm "text-lg mb-1")}
        (loc/tr :creator/contact-details)]
       (if (seq associations)
         [:div
          {:class (uu/cm "flex flex-col gap-2")}
          (doall
           (for [{:keys [association-key] :as association} associations
                 :let [setting (get-in association setting-path)
                       label (editor-utils/association-key-label
                              {:only-entity-type? nil}
                              association-key)
                       [source-entity-type _ _] association-key
                       association-edits @(rf/subscribe [m/associations-edits args])
                       value (get-in association-edits
                                     association-key)
                       {:keys [parties]} (existing-association-parties
                                          args association-key)
                       existing? (seq parties)
                       ;; existing associations satisfy mandatoriness => no red '*'
                       show-mandatory? (and (= setting :mandatory)
                                            (not existing?))]]
             ^{:key (str association-key)}
             [:div
              {:class (uu/cm
                       "flex relative gap-1.5 flex-col")}
              [input/label-elem
               {:label label
                :mandatory? show-mandatory?}]
              [existing-associations-view parties value]
              [data-input/view
               {:editor-type :editor.type/search
                :data-type :data.type/ref-multi
                :label (editor-utils/association-key-label
                        {:only-entity-type? nil #_TODO-count-different-other-entity-type}
                        association-key)
                :mandatory? show-mandatory?
                :value value
                :on-change (fn [_val opt-val]
                             (rf/dispatch [m/set-association
                                           (assoc args
                                                  :association-key association-key
                                                  :val opt-val)]))
                :placeholder (if existing?
                               (loc/tr :creator/add-more-from-the-list)
                               (loc/tr :creator-config/select-from-the-list))
                :create! (fn [query]
                           (rf/dispatch
                            [m/set-nested-creator
                             (assoc args
                                    :target-type :entity.type/person
                                    :initial-value query
                                    :creator-type :contact-creator
                                    ;; explicitly passed for nested-creator:
                                    :nested-on-ok!
                                    (fn [{:keys [new-entity-id new-entity close-cb!]}]
                                      (let [label (entity/display-string new-entity)
                                            opt-val {:value new-entity-id
                                                     :label label}
                                            next-val (conj (vec value) opt-val)]
                                        (rf/dispatch
                                         [m/set-association
                                          (assoc args
                                                 :association-key association-key
                                                 :val next-val)])
                                        (when (fn? close-cb!)
                                          (close-cb!))))
                                    :ctx {:association-key association-key})]))
                ;; prevent associating selected-target with itself:
                :exclude-values (when (and (= (:entity/type selected-target)
                                              :entity.type/person)
                                           (= source-entity-type
                                              :entity.type/person))
                                  #{(:db/id selected-target)})
                :field/entity-types [:entity.type/person]}]]))]
         [:div
          (loc/tr :creator/no-associations-configured)])])))

(defn title-view
  [args]
  (let [entity-type @(rf/subscribe [m/entity-type args])
        target @(rf/subscribe [m/selected-target args])
        target-name (when target
                      (entity/display-string target))
        default-title (loc/tr (default-title-loc-key entity-type))
        tooltip-text (if target-name
                       (loc/tr (create-for-target-loc-key entity-type)
                               [target-name])
                       default-title)
        subview @(rf/subscribe [m/subview args])
        t @(rf/subscribe [m/target-creator-type args])]
    (cond
      (= subview :overview)
      (loc/tr :creator/review-opportunity)

      (= subview :target-creator)
      [:div
       {:class (uu/cm "text-lg")}
       [breadcrumbs/view
        {:items [{:label default-title
                  :on-click (fn []
                              (rf/dispatch [m/navigate-back args]))}
                 {:label (str "/ " (loc/tr :creator/new-entity
                                           [(string/lower-case (etn/get-name t :en))]))
                  :active? true
                  :on-click (fn [_])}]}]]

      (= subview :contact-creator)
      [:div
       {:class (uu/cm "text-lg")}
       [breadcrumbs/view
        {:items [{:label default-title
                  :on-click (fn []
                              (rf/dispatch [m/navigate-back args]))}
                 {:label (str "/ " (loc/tr :new-contact))
                  :active? true
                  :on-click (fn [_])}]}]]

      :else
      [tooltip/as-child
       {}
       (fn [f-props]
         [:div
          (-> f-props
              (dissoc :className)
              (assoc :class (uu/cm "truncate text-lg")))
          default-title])
       tooltip-text])))

(defn get-creator-args
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        target-type @(rf/subscribe [m/target-type args])
        config-path (:main-config-path
                     (ecu/resolve-config-paths
                      {:entity-type entity-type
                       :target-type target-type
                       :pipeline pipeline}))
        creator-ident (m/resolve-creator-ident args)
        creator-args {:ident creator-ident
                      :entity-type entity-type
                      :target-type target-type
                      :config-path config-path
                      :skip-success-msg? true
                      :entity-edits-path creator-m/entity-edits-path}]
    creator-args))

(defn get-target-creator-args
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        stage @(rf/subscribe [m/stage args])
        target-type @(rf/subscribe [m/target-type args])
        config-path (:target-config-path
                     (ecu/resolve-config-paths
                      {:entity-type entity-type
                       :target-type target-type
                       :pipeline pipeline}))
        creator-ident (m/resolve-target-creator-ident args)
        creator-args {:ident creator-ident
                      :entity-type target-type
                      :target-type target-type
                      :pipeline pipeline
                      :stage stage
                      :config-path config-path
                      :skip-success-msg? true
                      :entity-edits-path creator-m/entity-edits-path}]
    creator-args))

(defn get-nested-creator-args
  [args target-type]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        stage @(rf/subscribe [m/stage args])
        target-type (or target-type
                        @(rf/subscribe [m/target-creator-type args])
                        @(rf/subscribe [m/target-type args]))
        config-path (:target-config-path
                     (ecu/resolve-config-paths
                      {:entity-type entity-type
                       :target-type target-type
                       :pipeline pipeline}))
        creator-ident (m/resolve-nested-creator-ident args)
        creator-args {:ident creator-ident
                      :entity-type target-type
                      :target-type target-type
                      :pipeline pipeline
                      :stage stage
                      :config-path config-path
                      :skip-success-msg? true
                      :entity-edits-path creator-m/entity-edits-path}]
    creator-args))

(defn get-target-profile-creator-args
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        stage @(rf/subscribe [m/stage args])
        target-type (or @(rf/subscribe [m/target-creator-type args])
                        @(rf/subscribe [m/target-type args]))
        ;;TODO later could be something else
        target-profile-type :entity.type/investor
        config-path (:target-profile-config-path
                     (ecu/resolve-config-paths
                      {:entity-type entity-type
                       :target-type target-type
                       :pipeline pipeline}))
        creator-ident (m/resolve-target-profile-creator-ident args)
        creator-args {:ident creator-ident
                      :entity-type target-profile-type
                      :pipeline pipeline
                      :stage stage
                      :config-path config-path
                      :skip-success-msg? true
                      :entity-edits-path creator-m/entity-edits-path}]
    creator-args))

(def investor-field-overrides
  {:investor/name {:read-only? true
                   :hidden? true}
   :investor/legal-entity {:read-only? true
                           :hidden? true}})

(defn entities-of-type
  [et]
  (get @session/entity-types et))

(defn lc-entity-names
  [entity-type]
  (let [entities @(r/track entities-of-type entity-type)
        name-attribute (registry/resolve-name-attribute entity-type)]
    (into
     #{}
     (map (fn [e]
            (-> e name-attribute cu/str-trim-lcase)))
     entities)))

(defn entity-name-exists?
  [entity-type entity-name]
  (let [lc-name (cu/str-trim-lcase entity-name)
        et-names @(r/track lc-entity-names entity-type)]
    (contains? et-names lc-name)))

(defn unique-entity-name
  ([entity-type entity-name]
   (if (entity-name-exists? entity-type entity-name)
     (unique-entity-name entity-type entity-name 2)
     entity-name))
  ([entity-type entity-name num]
   (let [name-try (str entity-name " (" num ")")]
     (if (entity-name-exists? entity-type name-try)
       (recur entity-type entity-name (inc num))
       name-try))))

(defn nested-creator-view
  [args]
  (r/with-let [pipeline @(rf/subscribe [m/pipeline args])
               entity-type @(rf/subscribe [m/entity-type args])
               target-type @(rf/subscribe [m/target-creator-type args])
               stage @(rf/subscribe [m/stage args])
               stages (->> (session/pipeline-stages pipeline)
                           (sort-by :common/ord))
               initial-value @(rf/subscribe [m/initial-target-value args])
               nested-on-ok! @(rf/subscribe [m/nested-on-ok args])
               ;; TODO: no longer used by target-creator (only the nested-creator)
               ;;       -remove after making sure everything works
               creator-ident (if (fn? nested-on-ok!)
                               (m/resolve-nested-creator-ident args)
                               (m/resolve-target-creator-ident args))
               creator-args (if (fn? nested-on-ok!)
                              ;;TODO ensure this is only used for person (for now)
                              (get-nested-creator-args args :entity.type/person)
                              (get-target-creator-args args))
               init-args (assoc creator-args
                                :pipeline pipeline
                                :skip-success-msg? true
                                :start-value initial-value
                                ;; See ui.completion.new-entity - waits for the
                                ;; search index for the types the new grid and
                                ;; kanban read out of OpenSearch, and for
                                ;; session sync for the bulk-synced ones.
                                :on-processed! new-entity/wait-for-searchable!
                                :on-ok! (if (fn? nested-on-ok!)
                                          nested-on-ok!
                                          ;; default: specific to target-creator:
                                          (fn [{:keys [new-entity-id new-entity close-cb!]}]
                                            (rf/dispatch
                                             [m/confirm-target
                                              (assoc args
                                                     :target-entity-id new-entity-id
                                                     ;; the creator's own response,
                                                     ;; since the new target will not
                                                     ;; arrive in @session/entities
                                                     :target-entity-data new-entity)])
                                            (when (fn? close-cb!)
                                              (close-cb!))))
                                :stage stage
                                :stages stages)
               _ (rf/dispatch-sync [creator-m/initialize init-args])]
    (let [pipeline @(rf/subscribe [m/pipeline args])
          config-path (:target-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type
                         :target-type target-type
                         :pipeline pipeline}))
          open-entity-fn @(rf/subscribe [m/open-entity-fn args])
          creator-args (assoc args
                              :ident creator-ident
                              :entity-type target-type
                              :open-entity-fn open-entity-fn
                              ;;:target-type target-type
                              :config-path config-path
                              :entity-edits-path creator-m/entity-edits-path)]
      [:div
       [creator/creator-view creator-args]])))

(defn opportunity-creator-view
  [args]
  (r/with-let [pipeline @(rf/subscribe [m/pipeline args])
               entity-type @(rf/subscribe [m/entity-type args])
               target-type @(rf/subscribe [m/target-type args])
               target @(rf/subscribe [m/selected-target args])
               target-id (:db/id target)
               target-name (entity/display-string target)
               name-try (str target-name " (" (:pipeline/name pipeline) ")")
               opportunity-name (unique-entity-name entity-type
                                                    name-try)
               stage @(rf/subscribe [m/stage args])
               stages (->> (session/pipeline-stages pipeline)
                           (sort-by :common/ord))
               creator-ident (m/resolve-creator-ident args)
               creator-args (get-creator-args args)
               name-attribute (registry/resolve-name-attribute entity-type)
               init-args (assoc creator-args
                                :pipeline pipeline
                                :stage stage
                                :stages stages
                                :initial-values
                                {name-attribute opportunity-name})
               previous-target @(rf/subscribe [m/creator-previous-target args])
               previous-target-id (:db/id previous-target)
               previous-target-type (:entity/type previous-target)
               previous-target-name (entity/display-string previous-target)
               initialize? (or (not= previous-target-type target-type)
                               (not= previous-target-id target-id)
                               (not previous-target-type))
               re-prefill-name? (and (not initialize?)
                                     (not= target-name previous-target-name)
                                     (string/includes?
                                      (or (get @(rf/subscribe [creator-m/entity-edits
                                                               creator-args])
                                               name-attribute) "")
                                      (or previous-target-name "")))
               _ (rf/dispatch [m/set-creator-previous-target args])
               _ (when initialize?
                   (rf/dispatch-sync [creator-m/initialize init-args]))
               _ (when re-prefill-name?
                   (rf/dispatch-sync [creator-m/edit-value
                                      (assoc creator-args
                                             :attribute name-attribute
                                             :new-value opportunity-name)]))]
    (let [pipeline @(rf/subscribe [m/pipeline args])
          config-path (:main-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type
                         :target-type target-type
                         :pipeline pipeline}))
          open-entity-fn @(rf/subscribe [m/open-entity-fn args])
          creator-args (assoc args
                              :ident creator-ident
                              :open-entity-fn open-entity-fn
                              :entity-type entity-type
                              :target-type target-type
                              :config-path config-path
                              :entity-edits-path creator-m/entity-edits-path)
          target @(rf/subscribe [m/selected-target args])
          target-name (if target
                        (entity/display-string target)
                        "-")]
      [:div
       [:div
        {:class (uu/cm "text-base font-semibold mt-4 mb-2")}
        (loc/tr :creator/opportunity)]
       [:div
        {:class (uu/cm "mb-4 p-3 bg-base-regular"
                       "border border-solid border-base-light-border-paler rounded")}
        (when-not target
          [input/view
           {:data-type :data.type/text
            :label (loc/tr (target-label-loc-key entity-type))
            :value target-name
            :class (uu/cm "mb-3"
                          "cursor-not-allowed")
            :disabled? true
            :read-only? true}])
        [creator/creator-view
         (assoc creator-args
                :field-schema-overrides
                {(registry/resolve-name-attribute entity-type)
                 {:field/translations
                  {:en (loc/tr :creator/opportunity-name)}}})]]])))

(defn target-creator
  [args]
  (r/with-let [pipeline @(rf/subscribe [m/pipeline args])
               entity-type @(rf/subscribe [m/entity-type args])
               target-type @(rf/subscribe [m/target-type args])
               target @(rf/subscribe [m/selected-target args])
               person? (= target-type :entity.type/person)
               target-name (entity/display-string target)
               stage @(rf/subscribe [m/stage args])
               stages (->> (session/pipeline-stages pipeline)
                           (sort-by :common/ord))
               creator-ident (m/resolve-target-creator-ident args)
               creator-args (get-target-creator-args args)
               name-attribute (registry/resolve-name-attribute target-type)

               initial-values (if person?
                                (select-keys target
                                             [:person/first-name :person/last-name
                                              :person/universal-email-address])
                                {name-attribute target-name})
               init-args (assoc creator-args
                                :pipeline pipeline
                                :stage stage
                                :stages stages
                                :initial-values
                                initial-values)
               previous-target @(rf/subscribe [m/creator-previous-target args])
               previous-target-type (:entity/type previous-target)
               previous-target-name (entity/display-string previous-target)
               initialize? (or (not= previous-target-type target-type)
                               (not previous-target-type))
               re-prefill-name? (and (not initialize?)
                                     (not person?)
                                     (not= target-name previous-target-name))
               _ (rf/dispatch [m/set-creator-previous-target args])
               _ (when initialize?
                   (rf/dispatch-sync [creator-m/initialize init-args]))
               _ (when re-prefill-name?
                   ;;TODO for person: m/edit-values select-keys [first,last,email]
                   (rf/dispatch-sync [creator-m/edit-value
                                      (assoc creator-args
                                             :attribute name-attribute
                                             :new-value target-name)]))]
    (let [pipeline @(rf/subscribe [m/pipeline args])
          config-path (:target-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type
                         :target-type target-type
                         :pipeline pipeline}))
          open-entity-fn @(rf/subscribe [m/open-entity-fn args])
          creator-args (assoc args
                              :ident creator-ident
                              :entity-type target-type
                              :open-entity-fn open-entity-fn
                              ;;:target-type target-type
                              :config-path config-path
                              :entity-edits-path creator-m/entity-edits-path)
          target-type @(rf/subscribe [m/target-type args])
          company? (= target-type :entity.type/company)]
      [:div
       [creator/creator-view
        (cond-> creator-args
          true (assoc :auto-focus-first-blank? true)
          company? (assoc :field-schema-overrides
                          {:asset/name
                           {:field/translations
                            {:en (loc/tr :company-name)}}}))]])))

(defn update-person-field-schemas
  [person field-schemas]
  (let [email (:person/universal-email-address person)]
    (if-not (cu/blank-value? email)
      (-> field-schemas
          (update :person/universal-email-address assoc :read-only? true))
      (-> field-schemas
          (update :person/first-name assoc :read-only? true)
          (update :person/last-name assoc :read-only? true)))))

(defn update-company-field-schemas
  [_company field-schemas]
  (-> field-schemas
      (assoc-in [:asset/name :field/translations :en]
                (loc/tr :company-name))))

(defn target-editor
  [{:keys [] :as args}]
  (r/with-let [pipeline @(rf/subscribe [m/pipeline args])
               target @(rf/subscribe [m/selected-target args])
               stage @(rf/subscribe [m/stage args])
               stages (->> (session/pipeline-stages pipeline)
                           (sort-by :common/ord))
               creator-ident (m/resolve-target-creator-ident args)
               creator-args (get-target-creator-args args)
               init-args (assoc creator-args
                                :entity target
                                :pipeline pipeline
                                :stage stage
                                :stages stages)
               previous-target @(rf/subscribe [m/creator-previous-target args])
               initialize? (or (not= previous-target target)
                               (not previous-target))
               _ (rf/dispatch [m/set-creator-previous-target args])
               _ (when initialize?
                   (rf/dispatch-sync [creator-m/initialize init-args]))]
    (let [entity-type @(rf/subscribe [m/entity-type args])
          target-type @(rf/subscribe [m/target-type args])
          target @(rf/subscribe [m/selected-target args])
          pipeline @(rf/subscribe [m/pipeline args])
          full-config (:pipeline/creator-config pipeline)
          config-path (:target-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type
                         :target-type target-type
                         :pipeline pipeline}))
          name-attribute (registry/resolve-name-attribute target-type)
          field-schemas (-> (registry/get-schema @session/tenant-schema target-type)
                            (assoc-in [name-attribute :read-only?] true))
          field-schemas (case target-type
                          :entity.type/person
                          (update-person-field-schemas target field-schemas)

                          :entity.type/company
                          (update-company-field-schemas target field-schemas)

                          field-schemas)
          editor-args (assoc args
                             :ident creator-ident
                             :entity-type target-type
                             ;;:target-type target-type
                             :config-path config-path
                             :entity-edits-path creator-m/entity-edits-path)]
      [:div
       [editor/view
        editor-args
        {:raw-config full-config
         :field-schemas field-schemas
         :entity target}]])))

(defn target-view
  [{:keys [] :as args}]
  (let [target @(rf/subscribe [m/selected-target args])
        target-id (:db/id target)
        creator? (string? target-id)
        editor? (number? target-id)]
    [:div
     (cond
       creator?
       [target-creator args]

       editor?
       [target-editor args])]))

(defn target-profile-creator
  [args]
  (r/with-let [pipeline @(rf/subscribe [m/pipeline args])
               entity-type @(rf/subscribe [m/entity-type args])
               target-type @(rf/subscribe [m/target-type args])
               target @(rf/subscribe [m/selected-target args])
               target-name (entity/display-string target)
               target-id (:db/id target)
               ;;opportunity-name (unique-entity-name entity-type target-name)
               stage @(rf/subscribe [m/stage args])
               stages (->> (session/pipeline-stages pipeline)
                           (sort-by :common/ord))
               creator-ident (m/resolve-target-profile-creator-ident args)
               creator-args (get-target-profile-creator-args args)
               target-profile @(rf/subscribe [m/target-profile args])
               target-profile-type :entity.type/investor
               name-attribute (registry/resolve-name-attribute target-profile-type)
               init-args (assoc creator-args
                                :pipeline pipeline
                                :stage stage
                                :stages stages
                                :initial-values
                                {name-attribute target-name
                                 :investor/legal-entity target-id})
               previous-target-profile @(rf/subscribe
                                         [m/creator-previous-target-profile args])
               overrides investor-field-overrides
               previous-target @(rf/subscribe [m/creator-previous-target args])
               previous-target-type (:entity/type previous-target)
               previous-target-name (entity/display-string previous-target)
               initialize? (or (not= previous-target-profile target-profile)
                               (not= previous-target-type target-type)
                               (not previous-target-type))
               re-prefill-name? (and (not initialize?)
                                     (not= target previous-target)
                                     (string/includes?
                                      (or (get @(rf/subscribe [creator-m/entity-edits
                                                               creator-args])
                                               name-attribute) "")
                                      (or previous-target-name "")))
               _ (rf/dispatch [m/set-creator-previous-target-profile args])
               _ (when initialize?
                   (rf/dispatch-sync [creator-m/initialize init-args]))
               _ (when re-prefill-name?
                   (rf/dispatch-sync [creator-m/edit-value
                                      (assoc creator-args
                                             :attribute name-attribute
                                             :new-value target-name)]))]
    (let [pipeline @(rf/subscribe [m/pipeline args])
          config-path (:target-profile-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type
                         :target-type target-type
                         :pipeline pipeline}))
          open-entity-fn @(rf/subscribe [m/open-entity-fn args])
          creator-args (assoc args
                              :ident creator-ident
                              :entity-type target-profile-type
                              :open-entity-fn open-entity-fn
                              :field-schema-overrides overrides
                              ;;:target-type target-type
                              :config-path config-path
                              :entity-edits-path creator-m/entity-edits-path)]
      [creator/creator-view
       creator-args])))

(defn target-profile-editor
  [{:keys [] :as args}]
  (r/with-let [pipeline @(rf/subscribe [m/pipeline args])
               target-profile @(rf/subscribe [m/target-profile args])
               stage @(rf/subscribe [m/stage args])
               stages (->> (session/pipeline-stages pipeline)
                           (sort-by :common/ord))
               creator-ident (m/resolve-target-profile-creator-ident args)
               creator-args (get-target-profile-creator-args args)
               init-args (assoc creator-args
                                :entity target-profile
                                :pipeline pipeline
                                :stage stage
                                :stages stages)
               previous-target-profile @(rf/subscribe
                                         [m/creator-previous-target-profile args])
               initialize? (or (not= previous-target-profile target-profile)
                               (not previous-target-profile))
               _ (rf/dispatch [m/set-creator-previous-target-profile args])
               _ (when initialize?
                   (rf/dispatch-sync [creator-m/initialize init-args]))]
    (let [entity-type @(rf/subscribe [m/entity-type args])
          target-type @(rf/subscribe [m/target-type args])
          pipeline @(rf/subscribe [m/pipeline args])
          target-profile-type (ecu/resolve-target-profile-type
                               {:entity-type entity-type
                                :target-type target-type
                                :pipeline pipeline})
          target-profile @(rf/subscribe [m/target-profile args])
          full-config (:pipeline/creator-config pipeline)
          config-path (:target-profile-config-path
                       (ecu/resolve-config-paths
                        {:entity-type entity-type
                         :target-type target-type
                         :pipeline pipeline}))
          field-schemas (registry/get-schema @session/tenant-schema
                                             target-profile-type)
          investor? (= target-profile-type :entity.type/investor)
          field-schema-overrides (when investor?
                                   investor-field-overrides)
          editor-args (assoc args
                             :ident creator-ident
                             :entity-type target-profile-type
                             :config-path config-path
                             :entity-edits-path creator-m/entity-edits-path)]
      [:div
       [editor/view
        editor-args
        {:raw-config full-config
         :field-schemas field-schemas
         :field-schema-overrides field-schema-overrides
         :entity target-profile}]])))

(defn target-profile-view
  [{:keys [] :as args}]
  (let [target-profile (or @(rf/subscribe [m/target-profile args])
                           {:db/id "new-target-profile"
                            :entity/type :entity.type/investor})
        target-profile-id (:db/id target-profile)
        creator? (string? target-profile-id)
        editor? (number? target-profile-id)]
    [:div
     (cond
       creator?
       [target-profile-creator args]

       editor?
       [target-profile-editor args])]))

(defn entity-creator-view
  [args]
  (let [entity-type @(rf/subscribe [m/entity-type args])
        target-type @(rf/subscribe [m/target-type args])
        pipeline @(rf/subscribe [m/pipeline args])
        target-profile-type (ecu/resolve-target-profile-type
                             {:pipeline pipeline
                              :entity-type entity-type
                              :target-type target-type})
        target-profile-id (:db/id @(rf/subscribe [m/target-profile args]))
        existing-target-profile? (number? target-profile-id)
        investor? (= target-profile-type :entity.type/investor)]
    [:div
     (when target-type
       [:<>
        [:div
         {:class (uu/cm "text-base font-semibold mb-1")}
         (loc/tr (target-title-loc-key entity-type))]
        [:div
         {:class (uu/cm "p-3 bg-base-regular"
                        "border border-solid border-base-light-border-paler rounded"
                        (when-not target-profile-type
                          "mb-4"))}
         [target-view args]]])
     (when target-profile-type
       [:<>
        (when (and investor?
                   existing-target-profile?)
          [:div
           {:class (uu/cm "flex flex-col items-start p-4 rounded shadow-md"
                          "bg-blue-50 text-blue-800"
                          "border-blue-400 border-l-4 mt-6")}
           [:div (loc/tr :info-note)]
           [:div (loc/tr :investor-creator/existing-investor-note
                         [(string/lower-case
                           (etn/get-name target-type :en))])]])
        (when investor?
          [:div
           {:class (uu/cm "text-base font-semibold mt-4 mb-1")}
           (loc/tr :creator/investor-profile)])
        [:div
         {:class (uu/cm "mb-4 p-3 bg-base-regular"
                        "border border-solid border-base-light-border-paler rounded")}
         [target-profile-view args]]])
     [opportunity-creator-view args]]))

(defn overview-added-contacts
  "Renders the contacts the user is about to add (from edits), or '-' if none.
   A contact that duplicates an existing association (its id is in `existing-ids`)
   is highlighted with a warning and a tooltip."
  [contacts existing-ids]
  (if (empty? contacts)
    [:div
     {:class (uu/cm "flex flex-col items-start gap-0.5 px-1")}
     "-"]
    [:div
     {:class (uu/cm "flex flex-col items-start gap-0.5")}
     (doall
      (for [{:keys [value label]} contacts]
        (if (contains? existing-ids value)
          ^{:key (str value)}
          [tooltip/view
           [:div
            {:class (uu/cm "flex items-center gap-1 self-start px-1 rounded"
                           "bg-warning-light text-warning-light-text")}
            label
            [:i.fal.fa-info-circle]]
           [:div (loc/tr :creator/duplicate-association-warning [label])]]
          ^{:key (str value)}
          [:div
           {:class (uu/cm "px-1")}
           label])))]))

(defn association-info
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        stage-id @(rf/subscribe [m/stage-id args])
        setting-path (if (and pipeline stage-id)
                       [:stages stage-id :setting]
                       [:create :setting])
        association-entity-types (resolve-association-entity-types args)
        config (->> @(rf/subscribe [m/associations-config args])
                    :associations
                    (filterv (fn [a]
                               (contains? ecu/visible-settings
                                          (get-in a setting-path))))
                    (filterv (fn [{[source-entity-type _] :association-key}]
                               (contains? association-entity-types
                                          source-entity-type))))
        association-edits @(rf/subscribe [m/associations-edits args])]
    (when (seq config)
      [:div
       [text/h2-custom
        {:class (uu/cm "text-base mt-4 mb-1")}
        (if (cu/one-element? config)
          (loc/tr :creator/contact)
          (loc/tr :creator/contacts))]
       [:div
        {:class (uu/cm "mb-4 bg-base-regular border border-solid border-base-light-border-paler rounded p-3")}
        [:div
         {:class (uu/cm "flex flex-col gap-2")}
         (doall
          (for [{:keys [association-key]} config
                :let [contacts (get-in association-edits association-key)
                      {:keys [parties]} (existing-association-parties
                                         args association-key)
                      existing-ids (->> parties (map :db/id) set)]]
            ^{:key (str association-key)}
            [:div
             [:div
              {:class (uu/cm "font-semibold")}
              (editor-utils/overview-association-key-label
               association-key)]
             (if (seq parties)
               [:div
                {:class (uu/cm "ml-1 flex flex-col gap-1")}
                [:div
                 [:div
                  {:class (uu/cm "text-sm font-semibold mb-0")}
                  (loc/tr :creator/current-contacts)]
                 [:div
                  {:class (uu/cm "flex flex-col items-start gap-0.5")}
                  (doall
                   (for [party parties]
                     ^{:key (str (:db/id party))}
                     [:div
                      {:class (uu/cm "px-1")}
                      (entity/display-string party)]))]]
                [:div
                 [:div
                  {:class (uu/cm "text-sm font-semibold mb-0")}
                  (loc/tr :creator/contacts-to-be-added)]
                 [overview-added-contacts contacts existing-ids]]]
               [:div
                {:class (uu/cm "ml-1")}
                [overview-added-contacts contacts #{}]])]))]]])))

(defn resolve-edited-entity
  "Returns an entity map that contains updated attributes if contained in edits;
   attribute-values from original entity otherwise"
  [original-entity entity-edits]
  (->> (into (set (keys original-entity))
             (keys entity-edits))
       (reduce (fn [result-entity attribute]
                 (let [value (if (contains? entity-edits attribute)
                               (get entity-edits attribute)
                               (get original-entity attribute))]
                   (assoc result-entity attribute value)))
               {})))

(defn entity-info
  [args {:keys [entity-type entity config-path creator-args
                field-schema-overrides]}]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        stage-id @(rf/subscribe [m/stage-id args])
        tenant-schema @(rf/subscribe [::session-sub/tenant-schema])
        entity-schema (registry/get-schema tenant-schema entity-type)
        entity-schema (if (seq field-schema-overrides)
                        (reduce-kv (fn [acc attribute overrides]
                                     (update acc attribute
                                             (fn [fs]
                                               (potpuri/deep-merge fs overrides))))
                                   entity-schema
                                   field-schema-overrides)
                        entity-schema)

        entity-config @(rf/subscribe [creator-m/config
                                      {:entity-type entity-type
                                       :pipeline pipeline
                                       :config-path config-path
                                       :pipeline-id (:db/id pipeline)}])
        fields-config (->> entity-config
                           :fields
                           (filterv (fn [fc]
                                      (contains? ecu/visible-settings
                                                 (get-in fc [:stages stage-id :setting])))))
        fields (->> fields-config
                    (filterv (fn [{:keys [attribute stages create]}]
                               (let [field-config (if stage-id
                                                    (get stages stage-id)
                                                    create)
                                     field-schema (get entity-schema attribute)]
                                 (not (creator-u/hidden-field?
                                       {:field-config field-config
                                        :field-schema field-schema
                                        :attribute attribute
                                        :entity-type entity-type
                                        :pipeline pipeline}))))))
        entity-edits @(rf/subscribe [creator-m/entity-edits creator-args])

        entity-values (if-not entity
                        entity-edits
                        (resolve-edited-entity entity entity-edits))]
    [:div
     [:div
      {:class (uu/cm "flex flex-col gap-2")}
      (if (seq fields)
        (doall
         (for [{:keys [attribute]} fields
               :let [field-schema (get entity-schema attribute)
                     data-type (:field/data-type field-schema)
                     search-type? (contains? data-types/ref-types data-type)
                     multi? (or (= data-type :data.type/keyword-multi)
                                (= data-type :data.type/ref-multi))]]
           ^{:key (str attribute)}
           [:div
            [:div
             {:class (uu/cm "font-semibold")}
             (loc/field-name attribute field-schema)]
            [:div
             {:class (uu/cm "ml-1 max-w-70")}
             (let [edited? (contains? entity-edits attribute)
                   existing? (contains? entity attribute)
                   ;;TODO new version display-data needed that works with option-values:
                   display-value (fu/display-data
                                  tenant-schema
                                  @(rf/subscribe [::session-sub/tenant])
                                  attribute
                                  field-schema
                                  entity-values
                                  :show-currency? true
                                  :passed-value (when search-type?
                                                  (if multi?
                                                    (some->> attribute
                                                             (get entity-edits)
                                                             (map :value))
                                                    (some->> attribute
                                                             (get entity-edits)
                                                             :value))))]
               (cond
                 ;; for future (explicit 'nil' in edits indicates clearing a value):
                 (and edited?
                      existing?
                      (nil? (get entity-edits attribute)))
                 (loc/tr :creator/value-cleared)

                 display-value
                 display-value

                 :else
                 "-"))]]))
        [:div
         {:class (uu/cm "text-sm m-0 text-base-light-text-paler")}
         (loc/tr :creator/no-fields-configured)])]]))

(defn opportunity-info
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        target-type @(rf/subscribe [m/target-type args])
        entity-config-path (:main-config-path
                            (ecu/resolve-config-paths
                             {:entity-type entity-type
                              :target-type target-type
                              :pipeline pipeline}))
        creator-args (get-creator-args args)]
    [:div
     {:class (uu/cm "mb-4 bg-base-regular border border-solid border-base-light-border-paler rounded p-3")}
     [entity-info args {:entity-type entity-type
                        :config-path entity-config-path
                        :creator-args creator-args}]]))

(defn existing-entity?
  [{:keys [db/id] :as _entity}]
  (number? id))

(defn target-info
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        target-type @(rf/subscribe [m/target-type args])
        target @(rf/subscribe [m/selected-target args])
        entity-config-path (:target-config-path
                            (ecu/resolve-config-paths
                             {:entity-type entity-type
                              :target-type target-type
                              :pipeline pipeline}))
        creator-args (get-target-creator-args args)]
    [:div
     [entity-info args {:entity-type target-type
                        :config-path entity-config-path
                        :entity (when (existing-entity? target)
                                  target)
                        :creator-args creator-args}]]))

(defn target-profile-info
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        target-type @(rf/subscribe [m/target-type args])
        target-profile-type :entity.type/investor
        entity-config-path (:target-profile-config-path
                            (ecu/resolve-config-paths
                             {:entity-type entity-type
                              :target-type target-type
                              :pipeline pipeline}))
        target-profile @(rf/subscribe [m/target-profile args])
        investor? (= target-profile-type :entity.type/investor)
        creator-args (get-target-profile-creator-args args)]
    [:div
     (when investor?
       [text/h2-custom
        {:class (uu/cm "text-base mt-4 mb-1")}
        (loc/tr :creator/investor-profile)])
     [:div
      {:class (uu/cm "mb-4 bg-base-regular border border-solid border-base-light-border-paler rounded p-3")}
      [entity-info args {:entity-type target-profile-type
                         :config-path entity-config-path
                         :entity target-profile
                         :creator-args creator-args
                         :field-schema-overrides
                         (when investor?
                           investor-field-overrides)}]]]))

(defn overview-view
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        target @(rf/subscribe [m/selected-target args])
        target-type @(rf/subscribe [m/target-type args])
        entity-type @(rf/subscribe [m/entity-type args])
        target-profile? (ecu/target-profile?
                         {:pipeline pipeline
                          :entity-type entity-type
                          :target-type target-type})]
    [:div
     (when target
       [:<>
        [text/h2-custom
         {:class (uu/cm "text-base mb-1")}
         (loc/tr (target-title-loc-key entity-type))]
        [:div
         {:class (uu/cm "mb-4 bg-base-regular border border-solid rounded"
                        "border-base-light-border-paler p-3")}
         [target-info args]]])
     (when (and target-profile?
                (or (= target-type :entity.type/company)
                    (= target-type :entity.type/person)))
       [target-profile-info args])
     [text/h2-custom
      {:class (uu/cm "text-base mt-4 mb-1")}
      (loc/tr :creator/opportunity)]
     [opportunity-info args]
     [association-info args]]))

(defn view
  [args]
  (r/with-let [investor-vehicle-state (r/atom {})]
    (let [subview @(rf/subscribe [m/subview args])]
      [:div
       {:class (uu/cm "mb-2 pb-2")}
       (case subview
         :select-target-entity
         [select-target-view (assoc args :local-state investor-vehicle-state)]

         ;;main entity creation:
         :entity-creator
         [entity-creator-view args]

         ;;TODO no longer used(?) remove
         :target-creator
         [nested-creator-view args]

         :contact-details
         [contact-details-view args]

         :contact-creator
         ;; distinguished from target-creator via explicit :on-ok! saved in rf-state:
         [nested-creator-view args]

         :overview
         [overview-view args]

         nil)])))

(defn select-target-footer
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        target-mandatory? (not (:pipeline/allow-without-target? pipeline))
        selected-target @(rf/subscribe [m/selected-target args])
        next-disabled? (and target-mandatory? (not selected-target))
        tooltip (when next-disabled?
                  (case entity-type
                    :entity.type/fundraising-opportunity
                    (loc/tr :creator/froppo-select-investor-tooltip)

                    :entity.type/sales-opportunity
                    (loc/tr :creator/sales-oppo-select-customer-tooltip)

                    :entity.type/oppo
                    (loc/tr :creator/oppo-select-target-asset-tooltip)

                    :entity.type/exit-opportunity
                    (loc/tr :creator/exit-oppo-select-buyer-tooltip)

                    (loc/tr :creator/select-target-tooltip)))]
    [:div
     {:class (uu/cm "flex gap-4")}
     (when (and (not target-mandatory?)
                (not selected-target))
       [button/secondary-hollow
        {:on-click (fn [_]
                     (rf/dispatch [m/confirm-target args]))
         :tooltip tooltip
         :disabled? next-disabled?}
        (loc/tr (without-target-loc-key entity-type))])
     (when selected-target
       [button/primary-solid
        {:on-click (fn [_]
                     (rf/dispatch [m/confirm-target (assoc args
                                                           :target-entity-id
                                                           (:db/id selected-target)
                                                           ;; the entity that was loaded
                                                           ;; when it was selected
                                                           :target-entity-data
                                                           selected-target)]))
         :tooltip tooltip
         :disabled? next-disabled?}
        (loc/tr :next)])]))

(defn entity-creator-tooltip
  [{:keys [entity-type target-type target-profile?
           creator-args target-creator-args target-profile-creator-args
           validations target-validations target-profile-validations]}]
  (let [entity-name (when target-type
                      (etn/get-name entity-type :en))
        target-name (when target-type
                      (etn/get-name target-type :en))
        target-profile-name (when target-profile?
                              (etn/get-name :entity.type/investor :en))
        entity-tooltip
        (creator-components/entity-validations-tooltip
         creator-args
         entity-name
         validations)
        target-tooltip
        (when target-validations
          (creator-components/entity-validations-tooltip
           target-creator-args
           target-name
           target-validations))
        target-profile-tooltip
        (when target-profile-validations
          (creator-components/entity-validations-tooltip
           target-profile-creator-args
           target-profile-name
           target-profile-validations))
        tooltip-items (->> [entity-tooltip
                            target-tooltip
                            target-profile-tooltip]
                           (filter some?))]
    (cond
      (empty? tooltip-items)
      nil

      (= 1 (count tooltip-items))
      (first tooltip-items)

      :else
      (into
       [:div]
       (map-indexed (fn [idx item]
                      [:div
                       {:class (when (< idx
                                        (dec (count tooltip-items)))
                                 "mb-2")}
                       item]))
       tooltip-items))))

(defn entity-creator-footer
  [args]
  (let [pipeline @(rf/subscribe [m/pipeline args])
        entity-type @(rf/subscribe [m/entity-type args])
        target-type @(rf/subscribe [m/target-type args])
        target-profile? (ecu/target-profile?
                         {:pipeline pipeline
                          :entity-type entity-type
                          :target-type target-type})
        target @(rf/subscribe [m/selected-target args])
        target-profile @(rf/subscribe [m/target-profile args])
        target-locked? @(rf/subscribe [m/target-locked? args])
        creator-args (get-creator-args args)
        validations @(rf/subscribe [creator-m/entity-validations creator-args])
        target-creator-args (when target-type
                              (cond-> (get-target-creator-args args)
                                (existing-entity? target)
                                (assoc :entity target)))
        target-validations (when target-creator-args
                             @(rf/subscribe
                               [creator-m/entity-validations
                                target-creator-args]))
        ;;note: see `ecu/target-profile?`: only investor for now:
        investor? target-profile?
        target-profile-creator-args (when target-profile?
                                      (cond-> (get-target-profile-creator-args args)
                                        (existing-entity? target-profile)
                                        (assoc :entity target-profile)

                                        investor?
                                        (assoc :field-schema-overrides
                                               investor-field-overrides)))
        target-profile-validations (when target-profile-creator-args
                                     @(rf/subscribe
                                       [creator-m/entity-validations
                                        target-profile-creator-args]))
        next-disabled? (or (seq validations)
                           (seq target-validations)
                           (seq target-profile-validations))
        tooltip-text (entity-creator-tooltip
                      {:entity-type entity-type
                       :target-type target-type
                       :target-profile? target-profile?
                       :creator-args creator-args
                       :target-creator-args target-creator-args
                       :target-profile-creator-args target-profile-creator-args
                       :validations validations
                       :target-validations target-validations
                       :target-profile-validations target-profile-validations})]
    [:div
     {:class (uu/cm "flex gap-4")}
     (when-not target-locked?
       [button/secondary-hollow
        {:on-click (fn [_]
                     (rf/dispatch [m/navigate-back args]))
         :disabled? false}
        (loc/tr :back)])
     [button/primary-solid
      {:on-click (fn [_]
                   (rf/dispatch [m/confirm-entity args]))
       :tooltip tooltip-text
       :disabled? next-disabled?}
      (loc/tr :next)]]))

(defn nested-creator-footer
  [args]
  (let [creator-args (-> (get-nested-creator-args args :entity.type/person)
                         (assoc :entity-edits-path creator-m/entity-edits-path))
        validations @(rf/subscribe [creator-m/entity-validations creator-args])
        processing? @(rf/subscribe [creator-m/processing? creator-args])
        next-disabled? (or (seq validations)
                           processing?)
        tooltip-text (creator-components/entity-validations-tooltip
                      creator-args
                      nil
                      validations)]
    [:div
     {:class (uu/cm "flex gap-4")}
     [button/secondary-hollow
      {:on-click (fn [_]
                   (rf/dispatch [m/navigate-back args]))
       :disabled? false}
      (loc/tr :back)]
     [button/primary-solid
      {:on-click (fn [_]
                   (rf/dispatch [creator-m/start-create
                                 creator-args]))
       :tooltip tooltip-text
       :disabled? next-disabled?}
      (if processing?
        (loc/tr :creator/creating)
        (loc/tr :create))]]))

(defn contact-details-footer
  [args]
  (let [edits @(rf/subscribe [m/associations-edits args])
        stage-id @(rf/subscribe [m/stage-id args])
        setting-path [:stages stage-id :setting]
        config @(rf/subscribe [m/associations-config args])
        association-entity-types (resolve-association-entity-types args)
        mandatory-associations (->> (:associations config)
                                    (filterv (fn [a]
                                               (= :mandatory
                                                  (get-in a setting-path))))
                                    (mapv :association-key)
                                    (filterv (fn [[source-entity-type _]]
                                               (contains? association-entity-types
                                                          source-entity-type))))
        mandatory-empty (->> mandatory-associations
                             (remove (fn [association-key]
                                       (or (get-in edits association-key)
                                           (seq (:parties
                                                 (existing-association-parties
                                                  args association-key)))))))
        mandatory-empty? (seq mandatory-empty)
        mandatory-empty-text (->> mandatory-empty
                                  (mapv (partial editor-utils/association-key-label
                                                 {:only-entity-type? nil}))
                                  (string/join ", "))
        tooltip-text (when mandatory-empty?
                       (loc/tr :creator/empty-mandatory-associations
                               [(if (cu/multiple? mandatory-empty)
                                  "s"
                                  "")
                                mandatory-empty-text]))]
    [:div
     {:class (uu/cm "flex gap-4")}
     [button/secondary-hollow
      {:on-click (fn [_]
                   (rf/dispatch [m/navigate-back args]))
       :disabled? false}
      (loc/tr :back)]
     [button/primary-solid
      {:on-click (fn [_]
                   (rf/dispatch [m/confirm-contact-details args]))
       :tooltip tooltip-text
       :disabled? mandatory-empty?}
      (loc/tr :next)]]))

(defn overview-footer
  [args]
  (let [create-disabled? @(rf/subscribe [m/processing? args])
        creator-args (get-creator-args args)]
    [:div
     {:class (uu/cm "flex gap-4")}
     [button/secondary-hollow
      {:on-click (fn [_]
                   (rf/dispatch [m/navigate-back args]))
       :disabled? false}
      (loc/tr :back)]
     [button/primary-solid
      {:on-click (fn [_]
                   (rf/dispatch [m/start-create (assoc args
                                                       :creator-args
                                                       creator-args)]))
       :disabled? create-disabled?}
      (loc/tr :create)]]))

(defn footer
  [args]
  (let [subview @(rf/subscribe [m/subview args])]
    (case subview
      :select-target-entity
      [select-target-footer args]

      :entity-creator
      [entity-creator-footer args]

      :target-creator
      [nested-creator-footer args]

      :contact-details
      [contact-details-footer args]

      :contact-creator
      [nested-creator-footer args]

      :overview
      [overview-footer args]

      nil)))

(defn add-ident
  [{:keys [ident] :as props}]
  (let [ident (if ident
                ident
                (-> "pipeline-entity-creator-"
                    gensym
                    str
                    keyword
                    vector))]
    (assoc props :ident ident)))

(defn curtain
  [{:keys [close-modal! props] :as _modal-props}]
  (let [portal-target-atom (atom nil)
        ;; if props don't contain :ident => generate one:
        props (add-ident props)
        close! (fn [_]
                 (rf/dispatch [m/close-creator props])
                 (close-modal!))]
    ;;TODO remove 'when-not' - used only for development time convenience
    (when-not (= [@(rf/subscribe [m/pipeline props])
                  @(rf/subscribe [m/stage props])]
                 [(:pipeline props) (:stage props)])
      ;;(prn (get-in @re-frame.db/app-db
      ;;             (:ident props)))
      (rf/dispatch-sync [m/initialize props {:on-ok! (:on-ok! props)
                                             :on-processed! (:on-processed! props)
                                             :close-modal! close-modal!}]))
    (fn [{:keys [shown? _topmost?]}]
      (let [finished? true;;@(rf/subscribe [m/created-entity props])
            props (assoc props :portal-target-atom portal-target-atom)]
        [modal/view
         ;; we want this visible even if not topmost, otherwise would cause
         ;; unwanted resetting of local-state
         {:visible?      shown?
          :hide!         (if finished?
                           (fn []
                             ;;(rf/dispatch [m/finish props])
                             (close! nil))
                           close!)
          :outer-content-class (uu/cm "justify-start")
          :header-child-class "truncate"
          :content-class (uu/cm
                          (if false
                            "w-9/12"
                            nil)
                          "mx-0 ml-auto h-full max-h-full mb-1"
                          "min-w-[400px] w-1/4")
          :close-button? true}
         [title-view props]
         [view props]
         [footer props]]))))

;; This one uses 'drawer' styled from zapkit-components.
;; TODO use when tw2 specific styles are finalized; i.e. rename this as 'curtain'
;;      and rename above as 'curtain-legacy' (or remove altogether)
(defn curtain-tw2
  [{:keys [close-modal! props] :as _modal-props}]
  (let [portal-target-atom (atom nil)
        props (add-ident props)
        close! (fn [_]
                 (rf/dispatch [m/close-creator props])
                 (close-modal!))]
    (when-not (= [@(rf/subscribe [m/pipeline props])
                  @(rf/subscribe [m/stage props])]
                 [(:pipeline props) (:stage props)])
      (rf/dispatch-sync [m/initialize props {:on-ok! (:on-ok! props)
                                             :on-processed! (:on-processed! props)
                                             :close-modal! close-modal!}]))
    (fn [{:keys [shown? _topmost?]}]
      (let [finished? true
            props (assoc props :portal-target-atom portal-target-atom)]
        [drawer/view
         {:visible? shown?
          :hide! (if finished?
                   (fn []
                     (close! nil))
                   close!)
          :prevent-overlay-close? true
          :class "tw2-min-w-[400px] tw2-w-1/4"
          :overlay-class "tw2-bg-black/50"
          :close-button? true}
         [title-view props]
         [view props]
         [footer props]]))))
